import type { EaseFn } from './types';

/** Linear - no easing */
export const linear: EaseFn = (u: number): number => {
    return u;
};

/** Quadratic ease in - starts slow */
export const easeIn: EaseFn = (u: number): number => {
    return u * u;
};

/** Quadratic ease out - ends slow */
export const easeOut: EaseFn = (u: number): number => {
    return u * (2 - u);
};

/** Quadratic ease in-out - slow at both ends */
export const easeInOut: EaseFn = (u: number): number => {
    return u < 0.5
        ? 2 * u * u
        : -1 + (4 - 2 * u) * u;
};

/** Circular ease in - starts very slow, accelerates */
export const easeCirc: EaseFn = (u: number): number => {
    return 1 - Math.sqrt(1 - u * u);
};

/** Back ease in - slight overshoot backwards, then forward */
export const easeBack: EaseFn = (u: number): number => {
    const s = 1.70158;
    return u * u * ((s + 1) * u - s);
};

/** Bounce ease out - bounces at the end */
export const easeBounce: EaseFn = (u: number): number => {
    const n1 = 7.5625;
    const d1 = 2.75;

    if (u < 1 / d1) {
        return n1 * u * u;
    } else if (u < 2 / d1) {
        return n1 * (u -= 1.5 / d1) * u + 0.75;
    } else if (u < 2.5 / d1) {
        return n1 * (u -= 2.25 / d1) * u + 0.9375;
    } else {
        return n1 * (u -= 2.625 / d1) * u + 0.984375;
    }
};

/** Elastic ease out - springs past target then settles */
export const easeElastic: EaseFn = (u: number): number => {
    const c4 = (2 * Math.PI) / 3;
    return u === 0
        ? 0
        : u === 1
        ? 1
        : Math.pow(2, -10 * u) * Math.sin((u * 10 - 0.75) * c4) + 1;
};

/** Overshoot ease in-out - overshoots at both ends */
export const overShoot: EaseFn = (u: number): number => {
    const s = 1.70158 * 1.525;
    if (u < 0.5) {
        return (Math.pow(2 * u, 2) * ((s + 1) * 2 * u - s)) / 2;
    } else {
        return (Math.pow(2 * u - 2, 2) * ((s + 1) * (u * 2 - 2) + s) + 2) / 2;
    }
};
